/**
 * Ryder One Landing Page Configuration
 * 
 * This is the default landing page (/) targeting crypto self-custody.
 * Based on the buy.ryder.id and ryder.id positioning.
 */

import type { PartialLandingPageConfig } from './types';
import { createConfig } from './utils';

const downAlternativePartial: PartialLandingPageConfig = {
  id: 'down-alternative',
  name: 'Ryder Crypto Wallet',
  slug: 'down-alternative',
  pageType: 'angle' as const,
  category: 'legacy',
  
  seo: {
    title: 'Ryder One - Your Crypto, Your Control',
    description: 'The only hardware wallet without the hard part. Protect your crypto with Ryder One, the simplest hardware wallet that can be set up in just 60 seconds.',
    keywords: ['crypto wallet', 'hardware wallet', 'bitcoin', 'ethereum', 'self-custody', 'web3', 'cryptocurrency', 'ryder one'],
  },
  
  hero: {
    headline: 'Ryder One \u2013 Your Crypto, Your Control',
    subheadline: 'The only hardware wallet without the hard part. Protect your crypto with Ryder One, set up in just 60 seconds. No stress.',
    trustBadge: {
      reviewCount: '5,000+',
      rating: '5 Star',
      source: 'Reviews',
    },
    usps: [
      { icon: 'shield', title: 'EAL6+ Security', description: 'Bank-grade chip protection' },
      { icon: 'support', title: 'Multi-Chain', description: 'BTC, ETH, SOL, STX + tokens' },
      { icon: 'hypoallergenic', title: 'TapSafe Recovery', description: 'Seed phrase optional' },
      { icon: 'clock', title: '60s Setup', description: 'Ready in under a minute' },
    ],
    showOekoTexBadge: false,
    showOprahBadge: false,
    show30NightsBadge: true,
  },
  
  gallery: {
    images: [
      { src: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/AWwuJtuLTrztkpjg.jpg', alt: 'Ryder One - My Wallet Screen', type: 'image' },
      { src: 'https://ryder.id/cdn/shop/videos/c/vp/2b3a4b4b5f1b4ca9828a44c06ebd0e46/2b3a4b4b5f1b4ca9828a44c06ebd0e46.HD-1080p-7.2Mbps-41642011.mp4?v=0', alt: 'Tap to Setup - 60 Second Wallet Creation', type: 'video', posterSrc: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/AWwuJtuLTrztkpjg.jpg' },
      { src: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/NjPBThSaodRgKVfu.png', alt: 'Ryder One - Backup Transferred', type: 'image' },
      { src: 'https://ryder.id/cdn/shop/videos/c/vp/0b8c3e4100ed465995115de9d7029fbb/0b8c3e4100ed465995115de9d7029fbb.HD-1080p-7.2Mbps-41642137.mp4?v=0', alt: 'Tap to Send - Secure Transactions', type: 'video', posterSrc: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/NjPBThSaodRgKVfu.png' },
      { src: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/ExeMYjYyFNdfJBGh.png', alt: 'Ryder One with Phone and Recovery Tag', type: 'image' },
      { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/DSC00548_e7ff9c90.jpg', alt: 'Ryder App with Ryder One Device', type: 'image' },
    ],
    showAwardCarousel: true,
    awardCarouselItems: [
      { title: 'Best Hardware Wallet', source: 'CoinDesk' },
      { title: 'Most User-Friendly', source: 'Decrypt' },
      { title: 'Top Security Pick', source: 'Bitcoin Magazine' },
      { title: 'Editor\'s Choice', source: 'The Block' },
      { title: '4.7/5 Rating', source: 'Trustpilot' },
    ],
  },
  
  benefits: {
    sectionTitle: 'The only hardware wallet without the hard part',
    sectionSubtitle: 'Self-custody shouldn\'t require a PhD in cryptography. Ryder One brings bank-level security to a device as simple as your phone.',
    benefits: [
      { icon: 'moon', title: 'One-Button Transactions', description: 'Physical confirmation button eliminates complex signing processes. Just review and press, your transaction is secure.', image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/sign-transaction_1e17dc93.png' },
      { icon: 'refresh', title: 'Recovery Made Simple', description: 'Your backup stays offline on the Recovery Tag. No seed phrases to memorize, no paper to lose.', image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/confirmed-recovery-tag_c7a96add.jpg' },
      { icon: 'sparkles', title: 'Multi-Chain Native', description: 'Bitcoin, Ethereum, Stacks, and Solana plus top ERC-20 and SPL tokens. One wallet for your portfolio.', image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/multi-chain_957a1d54.png' },
    ],
  },
  
  difference: {
    standardPillowTitle: 'Traditional Hardware Wallets',
    standardPillowPoints: [
      'Complex setup with 20+ steps',
      'Memorize 12-24 word seed phrases',
      'Lose your backup, lose your crypto',
      'Confusing interfaces and workflows',
      'Recovery is stressful and error-prone',
    ],
    fluffcoPillowTitle: 'Ryder One',
    fluffcoPillowPoints: [
      'Setup in 60 seconds with 3 taps',
      'Seed phrase optional — TapSafe handles backup for you',
      'Multiple recovery options (Tags + Phone + Contacts)',
      'Intuitive one-button interface',
      'Stress-free recovery, anytime',
    ],
  },
  
  testimonials: {
    sectionTitle: 'Trusted by industry leaders',
    sectionSubtitle: 'What the crypto community is saying',
    testimonials: [
      { name: 'Tim Draper', location: 'Draper Associates', verified: true, content: 'Ryder is making the simplest secure hard wallet in case you want total control over your Bitcoin and other cryptocurrencies.' },
      { name: 'Sean Carey', location: 'Crypto Analyst', verified: true, content: 'I am happy to say that Ryder is not vapor. Best hardware wallet experience I\'ve ever had.' },
      { name: 'Robinson', location: 'Co-Founder, Wormhole', verified: true, content: 'One of the coolest pieces of hardware I\'ve ever seen. The build quality and experience are unmatched.' },
      { name: 'Andres', location: 'Director of Partnerships, MoonPay', verified: true, content: 'We need more new ideas for self-custody. I ordered a Ryder One hardware wallet with day one support on Solana.' },
      { name: 'Joe Vezzani', location: 'CEO, LunarCrush', verified: true, content: 'Ryder is a paradigm shift in experience which will change the way you feel about hardware wallets forever.' },
      { name: 'Igor', location: 'Chief Growth Officer, Hacken', verified: true, content: 'The security architecture behind Ryder One is impressive. EAL6+ certification puts it in a league of its own for consumer hardware wallets.' },
      { name: 'Dayana', location: 'Media Lead, WalletConnect', verified: true, content: 'Finally a hardware wallet that doesn\'t make me feel like I need an engineering degree. The NFC tap experience is magical.' },
      { name: 'Yohan Lee', location: 'Crypto Investor', verified: true, content: 'My favorite hardware wallet! The setup was incredibly fast and the recovery system gives me peace of mind I never had with seed phrases.' },
      { name: 'Marcus', location: 'DeFi Developer', verified: true, content: 'I\'ve tested every major hardware wallet on the market. Ryder One is the only one I\'d recommend to my non-technical friends without hesitation.' },
      { name: 'Elena', location: 'Blockchain Consultant', verified: true, content: 'The TapSafe recovery system is genuinely innovative. No more anxiety about losing a piece of paper with my life savings on it.' },
      { name: 'Alex Chen', location: 'Web3 Engineer', verified: true, content: 'As a developer, I appreciate the thoughtful engineering. Air-gapped NFC with EAL6+ is the right approach to hardware wallet security.' },
      { name: 'Sarah K.', location: 'Crypto Portfolio Manager', verified: true, content: 'Switched from Ledger after 3 years. The difference in user experience is night and day. My clients love how simple it is to use.' },
    ],
    showMoreButton: true,
  },
  
  // FluffCo Secret Section - kept for compatibility but not used
  fluffcoSecret: {
    features: [],
  },
  
  // Sleep Experts Section - kept for compatibility but not used
  sleepExperts: {
    sectionTitle: '',
    sectionSubtitle: '',
    cards: [],
  },
  
  // FAQ Section with Ryder-specific questions
  faq: {
    sectionTitle: 'Common questions',
    items: [
      { question: 'How does Ryder One work?', answer: 'Ryder One connects to your phone via NFC (tap). Simply tap your device to set up, sign transactions, or recover your wallet. No cables, no Bluetooth, no Wi-Fi needed. The entire experience is designed to be as simple as tapping your phone.' },
      { question: 'What is TapSafe Recovery?', answer: 'TapSafe is Ryder\'s proprietary recovery system that replaces traditional 24-word seed phrases. Your backup is stored offline on a physical Recovery Tag. If you lose your wallet, simply tap the Recovery Tag to restore everything instantly. No writing, no memorizing, no stress.' },
      { question: 'Which cryptocurrencies does Ryder One support?', answer: 'Ryder One supports Bitcoin, Ethereum, Stacks, and Solana natively, plus top ERC-20 and SPL tokens. We\'re constantly adding new chains. The multi-chain architecture means you only need one device for all your crypto.' },
      { question: 'How secure is Ryder One?', answer: 'Ryder One uses the Infineon SLC38 secure element chip with EAL6+ certification, the same level of security trusted in passports and banking cards. Your private keys never leave the device. No Bluetooth, no Wi-Fi, no USB means zero wireless attack vectors.' },
      { question: 'What happens if I lose my Ryder One?', answer: 'Your crypto is safe. Use your Recovery Tag to restore your wallet on a new Ryder One device instantly. You can also set up phone recovery and trusted contacts as additional backup methods. Multiple layers of recovery ensure you never lose access.' },
      { question: 'What\'s included in the box?', answer: 'Every Ryder One order includes the Ryder One hardware wallet, plus a free welcome kit: Recovery Tag, Wireless Charger, and Travel Pouch \u2014 all included at no extra cost. You pay $199 (regularly $249), that\'s 20% off.' },
      { question: 'How long does setup take?', answer: 'Under 60 seconds. Seriously. Download the Ryder app, tap your Ryder One to your phone, and you\'re ready to go. No complex seed phrase writing, no firmware updates, no confusing menus.' },
    ],
  },
};

export const downAlternativeConfig = createConfig(downAlternativePartial);
export default downAlternativeConfig;
